import Dexie, { Table } from "dexie";
import * as asn1js from "asn1js";
import * as pkijs from "pkijs";
import * as forge from "node-forge";
import { Base64 } from "js-base64";
import canonicalize from "canonicalize";
//import { KeyFormat, KeyUsage } from "@simplewebauthn/browser/script/types/dom";

// public key
// exporting as hex like eth = 2x bigger!
// exporting as base64url = 33% bigger

export interface KeyPair {
    id: string;
    name: string;
    key: string; // derived value: public key base64url for querying
    publicKey: ArrayBuffer;
    privateKey: ArrayBuffer;
    createdAt: number;
    updatedAt: number;
    certificate?: ArrayBuffer | null;
}

type KeyPairSummary = Pick<
    KeyPair,
    "id" | "name" | "createdAt" | "updatedAt" | "key"
> & { publicKey: string; privateKey: string };

interface EncryptedData {
    encryptedSymmetricKey: string; // The AES key, encrypted with the RSA public key
    encryptedData: string; // The actual message, encrypted with the AES key
    iv: string; // The initialization vector used for AES-GCM encryption
}

interface EncryptedMessage {
    symmetricKey: CryptoKey;
    encryptedData: string;
    iv: string;
}

type Usage = "encrypt" | "sign";

export class Crypto extends Dexie {
    private keys: Table<KeyPair>;
    private readonly HBSK = "hbsk";
    private readonly HBIV = "hbiv";

    private encryptionAlgorithm = {
        name: "RSA-OAEP", // RSASSA-PKCS1-v1_5 = legacy support
        keyUsages: ["encrypt", "decrypt"] as KeyUsage[]
    };

    private signingAlgorithm = {
        name: "RSA-PSS",
        saltLength: 32,
        keyUsages: ["sign", "verify"] as KeyUsage[]
    };

    private asymmetricAlgorithm = {
        modulusLength: 2048, // or 3072 or 4096
        publicExponent: new Uint8Array([0x01, 0x00, 0x01]), // Uint8Array([1, 0, 1])
        e: 0x10001, // 65537 for forge and node
        hash: "SHA-256",
        keyUsages: ["encrypt", "decrypt", "sign", "verify"] as KeyUsage[], // cannot specify all 4 like node:crypto

        publicKeyEncoding: {
            type: "spki" as Exclude<KeyFormat, "jwk" | "raw">, // pem not supported
            format: "der" // default
        },
        publicKeyUsages: ["encrypt", "verify"] as KeyUsage[],

        privateKeyEncoding: {
            type: "pkcs8" as Exclude<KeyFormat, "jwk" | "raw">, // pem not supported
            format: "der" // default
        },
        privateKeyUsages: ["sign", "decrypt"] as KeyUsage[]
    };

    private symmetricAlgorithm = {
        name: "AES-GCM",
        length: 256, // or 128, 192
        keyUsages: ["encrypt", "decrypt"] as KeyUsage[]
    };

    constructor() {
        super("HeartBank");
        this.version(1).stores({
            keys: "id, name, key, createdAt, updatedAt"
        });
        this.keys = this.table("keys");
    }

    async count(): Promise<number> {
        return await this.keys.count();
    }

    /**
     * Generates a multi-purpose RSA key pair using node-forge and stores it
     * in the PKCS#8 format compatible with the Web Crypto API.
     */
    async add(name: string): Promise<string> {
        const id = window.crypto.randomUUID();
        const now = Date.now();

        // 1. Generate key pair using node-forge
        const forgeKeyPair = forge.pki.rsa.generateKeyPair({
            bits: this.asymmetricAlgorithm.modulusLength,
            e: this.asymmetricAlgorithm.e
        });

        // 2. Convert forge keys to standard DER format (as ArrayBuffer)
        const publicKeyDer = forge.pki.publicKeyToAsn1(forgeKeyPair.publicKey);

        // --- THIS IS THE FIX ---
        // Step A: Get the private key in its native (PKCS#1) ASN.1 format.
        const rsaPrivateKeyAsn1 = forge.pki.privateKeyToAsn1(
            forgeKeyPair.privateKey
        );
        // Step B: Wrap the PKCS#1 key in a PKCS#8 structure. This is what Web Crypto needs.
        const privateKeyDer = forge.pki.wrapRsaPrivateKey(rsaPrivateKeyAsn1);

        const publicKey = this.binaryStringToArrayBuffer(
            forge.asn1.toDer(publicKeyDer).getBytes()
        );
        const privateKey = this.binaryStringToArrayBuffer(
            forge.asn1.toDer(privateKeyDer).getBytes()
        );

        // 3. Store the DER ArrayBuffers in the database
        await this.keys.put({
            id,
            name,
            key: this.arrayBufferToBase64Url(publicKey),
            publicKey,
            privateKey,
            createdAt: now,
            updatedAt: now
        });

        return id;
    }

    async add2(
        name: string,
        publicKey: string,
        privateKey: string
    ): Promise<string | null> {
        const keyPair = await this.keys.get({ key: publicKey });
        if (keyPair) {
            return null;
        } else {
            const id = window.crypto.randomUUID();
            const now = Date.now();
            await this.keys.put({
                id,
                name,
                key: publicKey,
                publicKey: this.base64UrlToArrayBuffer2(publicKey),
                privateKey: this.base64UrlToArrayBuffer2(privateKey),
                createdAt: now,
                updatedAt: now
            });
            return id;
        }
    }

    async getName(key: string): Promise<string | null> {
        const keyPair = await this.keys.get({ key });
        return keyPair ? keyPair.name : null;
    }

    async getKeyId(key: string): Promise<string | null> {
        const keyPair = await this.keys.get({ key }); // this.keys.where("key").equals(key).first();
        //const keys = await this.getAllKeys();
        //keys.forEach((item) => console.log(item.key));
        return keyPair ? keyPair.id : null;
    }

    async getPublicKeyString(id: string): Promise<string> {
        const keyPair = await this.keys.get(id);
        if (!keyPair) throw new Error("Key not found");
        return this.arrayBufferToBase64Url(keyPair.publicKey);
    }

    async getPublicKey(key: string, usage: Usage): Promise<CryptoKey> {
        return await window.crypto.subtle.importKey(
            this.asymmetricAlgorithm.publicKeyEncoding.type,
            this.base64UrlToArrayBuffer(key) as BufferSource,
            {
                name:
                    usage === "encrypt"
                        ? this.encryptionAlgorithm.name
                        : this.signingAlgorithm.name,
                hash: this.asymmetricAlgorithm.hash
            },
            true,
            usage === "sign" ? ["verify"] : ["encrypt"]
        );
    }

    async getPrivateKey(key: string, usage: Usage): Promise<CryptoKey> {
        return await window.crypto.subtle.importKey(
            this.asymmetricAlgorithm.privateKeyEncoding.type,
            this.base64UrlToArrayBuffer(key) as BufferSource,
            {
                name:
                    usage === "encrypt"
                        ? this.encryptionAlgorithm.name
                        : this.signingAlgorithm.name,
                hash: this.asymmetricAlgorithm.hash
            },
            true,
            usage === "sign" ? ["sign"] : ["decrypt"]
        );
    }

    /**
     * Retrieves keys from the database and imports them as Web Crypto CryptoKey objects
     * for a specific purpose (usage). This method correctly assigns key usages.
     */
    async get(id: string, usage: Usage): Promise<CryptoKeyPair> {
        const keyPair = await this.keys.get(id);
        if (!keyPair) throw new Error("Key not found");

        // Define the correct algorithm and key usages based on the intended operation.
        const algorithm = {
            name:
                usage === "encrypt"
                    ? this.encryptionAlgorithm.name
                    : this.signingAlgorithm.name,
            hash: this.asymmetricAlgorithm.hash
        };

        // Import the public key with its correct usage.
        const publicKey = await window.crypto.subtle.importKey(
            this.asymmetricAlgorithm.publicKeyEncoding.type,
            keyPair.publicKey,
            algorithm,
            true, // extractable
            usage === "sign" ? ["verify"] : ["encrypt"]
        );

        // Import the private key with its correct usage.
        const privateKey = await window.crypto.subtle.importKey(
            this.asymmetricAlgorithm.privateKeyEncoding.type,
            keyPair.privateKey,
            algorithm,
            true, // extractable
            usage === "sign" ? ["sign"] : ["decrypt"]
        );

        return { publicKey, privateKey };
    }

    async rename(id: string, newName: string): Promise<void> {
        const keyPair = await this.keys.get(id);
        if (!keyPair) throw new Error("Key not found");
        keyPair.name = newName;
        keyPair.updatedAt = Date.now();
        await this.keys.put(keyPair);
    }

    async remove(id: string): Promise<void> {
        await this.keys.delete(id);
    }

    async deleteAllKeys(): Promise<void> {
        await this.keys.clear();
    }

    async getAllKeys(options?: {
        filter?: (record: KeyPair) => boolean;
        sort?: (a: KeyPair, b: KeyPair) => number;
    }): Promise<KeyPairSummary[]> {
        let keyPairs = await this.keys.toArray();

        if (options?.filter) {
            keyPairs = keyPairs.filter(options.filter);
        }

        if (options?.sort) {
            keyPairs = keyPairs.sort(options.sort);
        }

        return keyPairs.map(
            ({
                id,
                name,
                key,
                createdAt,
                updatedAt,
                publicKey,
                privateKey
            }) => ({
                id,
                name,
                key,
                createdAt,
                updatedAt,
                publicKey: this.arrayBufferToBase64Url(publicKey), // this.encodePEM(publicKey, "PUBLIC KEY")
                privateKey: this.arrayBufferToBase64Url(privateKey) // this.encodePEM(publicKey, "PUBLIC KEY")
            })
        );
    }

    // TODO: maybe fix to allow multiple keys
    /**
     * Creates a self-signed X.509 certificate using node-forge.
     * @param keyPair The KeyPair object from the database.
     * @returns A Promise that resolves to the certificate in DER format (ArrayBuffer).
     */
    // private async _createSelfSignedCert2(
    //     keyPair: KeyPair
    // ): Promise<ArrayBuffer> {
    //     const cert = forge.pki.createCertificate();

    //     // Convert stored ArrayBuffers back to forge key objects
    //     const publicKey = forge.pki.publicKeyFromAsn1(
    //         forge.asn1.fromDer(
    //             this.arrayBufferToBinaryString(keyPair.publicKey)
    //         )
    //     );
    //     const privateKey = forge.pki.privateKeyFromAsn1(
    //         forge.asn1.fromDer(
    //             this.arrayBufferToBinaryString(keyPair.privateKey)
    //         )
    //     );

    //     cert.publicKey = publicKey;
    //     cert.serialNumber = forge.util.bytesToHex(
    //         forge.random.getBytesSync(16)
    //     );
    //     cert.validity.notBefore = new Date();
    //     cert.validity.notAfter = new Date();
    //     cert.validity.notAfter.setFullYear(
    //         cert.validity.notBefore.getFullYear() + 10
    //     );

    //     const attrs = [{ name: "commonName", value: keyPair.name }];
    //     cert.setSubject(attrs);
    //     cert.setIssuer(attrs);

    //     cert.setExtensions([
    //         { name: "basicConstraints", cA: true },
    //         {
    //             name: "keyUsage",
    //             digitalSignature: true,
    //             keyCertSign: true,
    //             crlSign: true
    //         }
    //     ]);

    //     cert.sign(privateKey, forge.md.sha256.create());

    //     const certDer = forge.pki.certificateToAsn1(cert);
    //     return this.binaryStringToArrayBuffer(
    //         forge.asn1.toDer(certDer).getBytes()
    //     );
    // }

    // open mac key access and look under login!
    /**
     * Creates a self-signed X.509 certificate for a given key pair.
     * @param publicKeyDer The public key in SPKI DER format (ArrayBuffer).
     * @param privateKey The private key as a CryptoKey object for signing.
     * @param subjectName The Common Name (CN) for the certificate's subject/issuer.
     * @returns A Promise that resolves to the certificate in DER format (ArrayBuffer).
     */
    private async _createSelfSignedCert(
        keyPair: KeyPair
    ): Promise<ArrayBuffer> {
        const certificate = new pkijs.Certificate();

        // --- Basic Certificate fields ---
        certificate.version = 2; // X.509 v3

        // Generate a random 16-byte serial number
        const serialNumber = new Uint8Array(16);
        crypto.getRandomValues(serialNumber);
        certificate.serialNumber = new asn1js.Integer({
            valueHex: serialNumber
        });

        // --- Issuer and Subject (same for self-signed) ---
        // We'll use the key's name as the Common Name (CN)
        const commonName = new pkijs.AttributeTypeAndValue({
            type: "2.5.4.3", // OID for Common Name
            value: new asn1js.PrintableString({ value: keyPair.name })
        });
        certificate.issuer.typesAndValues.push(commonName);
        certificate.subject.typesAndValues.push(commonName);

        // --- Validity Period (e.g., 10 years) ---
        certificate.notBefore.value = new Date();
        certificate.notAfter.value = new Date();
        certificate.notAfter.value.setFullYear(
            certificate.notAfter.value.getFullYear() + 10
        );

        const cryptoKeyPair = await this.get(keyPair.id, "sign");

        // --- Public Key ---
        await certificate.subjectPublicKeyInfo.importKey(
            cryptoKeyPair!.publicKey
        );

        // --- Extensions ---
        // These are crucial for defining what the certificate can be used for.
        const basicConstraints = new pkijs.BasicConstraints({
            cA: true, // This is a CA certificate (since it signs itself)
            pathLenConstraint: 0 // No intermediate CAs are allowed under this one
        });

        // Bitmask: digitalSignature, keyCertSign, crlSign (bits 0, 5, 6)
        const keyUsageBits = new Uint8Array([0b10000110]);
        const keyUsage = new asn1js.BitString({
            valueHex: keyUsageBits.buffer
        });

        certificate.extensions = [
            new pkijs.Extension({
                extnID: "2.5.29.19", // basicConstraints
                critical: true,
                extnValue: basicConstraints.toSchema().toBER(false)
            }),
            new pkijs.Extension({
                extnID: "2.5.29.15", // keyUsage
                critical: true,
                extnValue: keyUsage.toBER(false)
            })
        ];

        // --- Signing the Certificate ---
        // The certificate is signed by its own private key.
        await certificate.sign(
            cryptoKeyPair!.privateKey,
            this.asymmetricAlgorithm.hash
        );

        // Return the certificate as a DER-encoded ArrayBuffer
        return certificate.toSchema(true).toBER(false);
    }

    /**
     * Exports all keys and certificates. This is mostly unchanged, but it now
     * relies on the new _createSelfSignedCert method.
     */
    async exportAll(): Promise<void> {
        const keyPairs = await this.keys.toArray();
        const promises = keyPairs.map(async (keyPair) => {
            const certificateDer = await this._createSelfSignedCert(keyPair);
            return {
                ...keyPair,
                certificate: certificateDer
            };
        });

        const keysWithCerts = await Promise.all(promises);
        const lines: string[] = [];

        for (const keyPair of keysWithCerts) {
            const pubPEM = this.encodePEM(keyPair.publicKey, "PUBLIC KEY");
            const privPEM = this.encodePEM(keyPair.privateKey, "PRIVATE KEY");
            const certPEM = this.encodePEM(keyPair.certificate!, "CERTIFICATE");

            lines.push(
                `// Key Pair & Certificate: ${keyPair.name} (${keyPair.id})`
            );
            lines.push(
                `// Created: ${new Date(keyPair.createdAt).toISOString()}`
            );
            lines.push(certPEM, pubPEM, privPEM, "");
        }
        const blob = new Blob([lines.join("\n")], { type: "text/plain" });
        this.downloadBlob(blob, "HeartBank.B.pem");
    }

    // TODO: verify that each key pair is valid before importing!!
    // TODO: check for .B.pem ??
    async importFromPEM(file: File): Promise<void> {
        const text = await file.text();
        const chunks = text.trim().split(/(?=^\/\/ Key Pair & Certificate:)/m);

        for (const chunk of chunks) {
            if (!chunk.trim()) continue;

            const metadataMatch = chunk.match(
                /^\/\/ Key Pair & Certificate: (.*?) \((.*?)\)\s*\n\/\/ Created: (.*?)$/m
            );
            const certMatch = chunk.match(
                /-----BEGIN CERTIFICATE-----\s*([\s\S]*?)\s*-----END CERTIFICATE-----/
            );
            const pubMatch = chunk.match(
                /-----BEGIN PUBLIC KEY-----\s*([\s\S]*?)\s*-----END PUBLIC KEY-----/
            );
            const privMatch = chunk.match(
                /-----BEGIN PRIVATE KEY-----\s*([\s\S]*?)\s*-----END PRIVATE KEY-----/
            );

            if (!metadataMatch || !certMatch || !pubMatch || !privMatch) {
                console.warn(
                    "Skipping malformed or incomplete entry in PEM file."
                );
                continue;
            }

            const [, name, id, createdAtString] = metadataMatch;
            const createdAt = new Date(createdAtString).getTime();

            const existing = await this.keys.get(id);
            if (existing) {
                console.log(`Skipping existing key with ID: ${id}`);
                continue;
            }

            // --- FINAL FIX ---
            // 1. Clean the extracted Base64 content by removing all whitespace.
            const cleanPublicKeyBase64 = pubMatch[1].replace(/\s/g, "");
            const cleanPrivateKeyBase64 = privMatch[1].replace(/\s/g, "");
            const cleanCertificateBase64 = certMatch[1].replace(/\s/g, "");

            // 2. Store the clean Base64 string and decode from the clean string.
            await this.keys.put({
                id,
                name,
                createdAt,
                updatedAt: createdAt,
                // Store the CLEAN, single-line Base64 string
                key: cleanPublicKeyBase64,
                // Decode the ArrayBuffers from the CLEAN strings
                publicKey: this.decodePEM(cleanPublicKeyBase64),
                privateKey: this.decodePEM(cleanPrivateKeyBase64),
                certificate: this.decodePEM(cleanCertificateBase64)
            });
        }
    }

    private encodePEM(buffer: ArrayBuffer, label: string): string {
        // 1. Convert the binary data directly to a URL-safe Base64 string
        //    using your reliable helper function.
        const base64Url = this.arrayBufferToBase64Url(buffer);

        // 2. Chunk the Base64 string into standard 64-character lines for the PEM body.
        const pemBody = base64Url.match(/.{1,64}/g)?.join("\n") || "";

        // 3. Add the standard PEM headers and footers.
        return `-----BEGIN ${label}-----\n${pemBody}\n-----END ${label}-----`;
    }

    /**
     * Decodes a full PEM-formatted string into an ArrayBuffer.
     *
     * @param pemString The full PEM string, including header and footer.
     * @returns The decoded binary data as a guaranteed ArrayBuffer.
     */
    private decodePEM(pemString: string): ArrayBuffer {
        // Step 1: Isolate the Base64 content from the PEM block by removing
        // the headers, footers, and all whitespace.
        const base64Content = pemString
            .replace(/-----BEGIN .*-----/, "")
            .replace(/-----END .*-----/, "")
            .replace(/\s/g, "");

        // Step 2: Decode the clean string using our simplified helper.
        const uint8Array = this.base64UrlToArrayBuffer(base64Content);

        // Step 3: Return the underlying buffer, asserting its type as ArrayBuffer.
        // This is safe because we know the library produces a standard ArrayBuffer.
        return uint8Array.buffer as ArrayBuffer;
    }

    private downloadBlob(blob: Blob, filename: string) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }

    //// signing/encryption ////

    async sign(id: string, data: string): Promise<string> {
        const keyPair = await this.get(id, "sign");
        if (!keyPair?.privateKey) throw new Error("Private key not found");
        // already hashes internally
        //const hash = await this.singleHash(data); //canonicalize(data)!
        const signature = await window.crypto.subtle.sign(
            {
                name: this.signingAlgorithm.name,
                saltLength: this.signingAlgorithm.saltLength
            },
            keyPair.privateKey,
            this.stringToArrayBuffer(data) //hash as BufferSource
        ); //ArrayBuffer

        return this.arrayBufferToBase64Url(signature);
    }

    async sign2(key: string, data: string): Promise<string> {
        const privateKey = await this.getPrivateKey(key, "sign");
        //const hash = await this.singleHash(data); //canonicalize(data)!
        // already hashes internally
        const signature = await window.crypto.subtle.sign(
            {
                name: this.signingAlgorithm.name,
                saltLength: this.signingAlgorithm.saltLength
            },
            privateKey,
            this.stringToArrayBuffer(data) //hash as BufferSource
        ); //ArrayBuffer

        return this.arrayBufferToBase64Url(signature);
    }

    async verify(
        key: string,
        data: string,
        signature: string
    ): Promise<boolean> {
        const publicKey = await this.getPublicKey(key, "sign");

        return await window.crypto.subtle.verify(
            {
                name: this.signingAlgorithm.name,
                saltLength: this.signingAlgorithm.saltLength
            },
            publicKey,
            this.base64UrlToArrayBuffer(signature) as BufferSource,
            this.stringToArrayBuffer(data)
        );
    }

    // for debugging
    async verify2(
        key: string,
        data: string,
        signature: string
    ): Promise<boolean> {
        const publicKey = await this.getPublicKey(key, "sign");
        //const hash = await this.singleHash(data); //canonicalize(data)!

        // --- LOGGING FOR DEBUGGING ---

        // A. Export back to SPKI/DER and re-encode to base64url
        // This should exactly match the original 'key' string.
        const exportedSpkiBuffer = await window.crypto.subtle.exportKey(
            "spki",
            publicKey
        );
        console.log(
            "Frontend Key (re-exported SPKI):",
            this.arrayBufferToBase64Url(exportedSpkiBuffer)
        );

        // B. Export to JWK for a human-readable version
        //const jwk = await window.crypto.subtle.exportKey("jwk", publicKey);
        //console.log("Frontend Key (JWK canonical):", canonicalize(jwk));

        // checking hash
        console.log(data);
        const browserHashString = await this.hash(data); // Assuming this.hash() produces a base64url string
        console.log("Browser Frontend Hash:", browserHashString);

        // --- END LOGGING ---

        return await window.crypto.subtle.verify(
            {
                name: this.signingAlgorithm.name,
                saltLength: this.signingAlgorithm.saltLength
            },
            publicKey,
            this.base64UrlToArrayBuffer(signature) as BufferSource,
            this.stringToArrayBuffer(data) //hash as BufferSource
        );
    }

    async isValid(publicKey: string, privateKey: string): Promise<boolean> {
        const signature = await this.sign2(privateKey, `{ m: "HeartBank" }`);
        return this.verify(publicKey, `{ m: "HeartBank" }`, signature);
    }

    async isValid2(publicKey: string): Promise<boolean> {
        const id = await this.getKeyId(publicKey);
        if (id) {
            const signature = await this.sign(id, `{ m: "HeartBank" }`);
            return this.verify(publicKey, `{ m: "HeartBank" }`, signature);
        } else return false;
    }

    /////

    hasSkAndIv() {
        return (
            sessionStorage.getItem(this.HBSK) &&
            sessionStorage.getItem(this.HBIV)
        );
    }

    async checkSkAndIV() {
        if (!sessionStorage.getItem(this.HBSK)) await this.generateAndSaveSk();
        if (!sessionStorage.getItem(this.HBIV)) await this.generateAndSaveIV();
    }

    private async generateAndSaveSk(): Promise<CryptoKey> {
        // 1. Generate Key
        const key = await window.crypto.subtle.generateKey(
            this.symmetricAlgorithm,
            true, // Extractable
            this.symmetricAlgorithm.keyUsages
        );

        // 2. Export as RAW (ArrayBuffer)
        const exportedBuffer = await window.crypto.subtle.exportKey("raw", key);

        // 3. Convert ArrayBuffer to Base64 String
        const base64Key = this.arrayBufferToBase64Url(exportedBuffer);

        // 4. Save String to SessionStorage
        sessionStorage.setItem(this.HBSK, base64Key);

        console.log("Raw key generated, encoded to Base64, and saved.");
        return key;
    }

    public getSK(): string {
        return sessionStorage.getItem(this.HBSK)!;
    }

    public async getSKFromSession(sk = ""): Promise<CryptoKey> {
        // 1. Get Base64 String
        //const base64Key = sessionStorage.getItem(this.HBSK)!;

        // 2. Convert Base64 String back to ArrayBuffer
        const keyBuffer = this.base64UrlToArrayBuffer(
            sk || this.getSK()
        ) as BufferSource;

        // 3. Import Key
        const cryptoKey = await window.crypto.subtle.importKey(
            "raw",
            keyBuffer,
            { name: this.symmetricAlgorithm.name },
            true,
            this.symmetricAlgorithm.keyUsages
        );

        console.log("Raw key successfully restored.");
        return cryptoKey;
    }

    // not used
    private async getSKFromSession2(): Promise<CryptoKey | null> {
        try {
            // 1. Get Base64 String
            const base64Key = sessionStorage.getItem(this.HBSK);

            if (!base64Key) {
                console.warn("No key found in session.");
                return null;
            }

            // 2. Convert Base64 String back to ArrayBuffer
            const keyBuffer = this.base64UrlToArrayBuffer(
                base64Key
            ) as BufferSource;

            // 3. Import Key
            const cryptoKey = await window.crypto.subtle.importKey(
                "raw",
                keyBuffer,
                { name: this.symmetricAlgorithm.name },
                true,
                this.symmetricAlgorithm.keyUsages
            );

            console.log("Raw key successfully restored.");
            return cryptoKey;
        } catch (error) {
            console.error("Error restoring raw key:", error);
            return null;
        }
    }

    /**
     * Generates a random 12-byte IV, converts to Base64, and saves to SessionStorage.
     */
    private generateAndSaveIV(): Uint8Array {
        // 1. Generate 12 random bytes
        const iv = window.crypto.getRandomValues(new Uint8Array(12));

        // 2. Convert the underlying buffer to Base64
        const base64IV = this.arrayBufferToBase64Url(iv.buffer);

        // 3. Store
        sessionStorage.setItem(this.HBIV, base64IV);

        console.log("IV generated and saved.");
        return iv;
    }

    public getIV(): string {
        return sessionStorage.getItem(this.HBIV)!;
    }

    /**
     * Retrieves the IV string from SessionStorage and converts back to Uint8Array.
     */
    private getIVFromSession(iv = ""): Uint8Array {
        // 1. Get String
        //const base64IV = sessionStorage.getItem(this.HBIV)!;

        // 2. Convert Base64 back to ArrayBuffer
        const buffer = this.base64UrlToArrayBuffer(iv || this.getIV());

        // 3. Return as Uint8Array (required for most crypto operations)
        return new Uint8Array(buffer);
    }

    // not used
    private getIVFromSession2(): Uint8Array | null {
        // 1. Get String
        const base64IV = sessionStorage.getItem(this.HBIV);

        if (!base64IV) {
            console.warn("No IV found in session.");
            return null;
        }

        try {
            // 2. Convert Base64 back to ArrayBuffer
            const buffer = this.base64UrlToArrayBuffer(base64IV);

            // 3. Return as Uint8Array (required for most crypto operations)
            return new Uint8Array(buffer);
        } catch (e) {
            console.error("Failed to decode IV", e);
            return null;
        }
    }

    async encryptMessage3(plainText: string): Promise<string> {
        const symmetricKey = await this.getSKFromSession();
        const iv = this.getIVFromSession();

        const ciphertextBuffer = await window.crypto.subtle.encrypt(
            {
                name: this.symmetricAlgorithm.name,
                iv: iv as BufferSource
            },
            symmetricKey,
            this.stringToArrayBuffer(plainText)
        );

        return this.arrayBufferToBase64Url(ciphertextBuffer); //encryptedData
    }

    /**
     * Calculates the hash of a file using the Web Crypto API.
     *
     * @param file - The File object to hash.
     * @param algorithm - The hashing algorithm to use (default: 'SHA-256').
     * @returns A Promise that resolves to the hexadecimal hash string.
     */
    async hashFile(
        file: File,
        algorithm: "SHA-1" | "SHA-256" | "SHA-384" | "SHA-512" = "SHA-256"
    ): Promise<string> {
        // 1. Check if Web Crypto is supported
        if (!crypto || !crypto.subtle) {
            throw new Error(
                "Web Crypto API is not supported in this environment."
            );
        }

        // 2. Read the file as an ArrayBuffer
        // Note: .arrayBuffer() loads the whole file into memory.
        // For multi-GB files, this may crash the browser tab.
        // It works perfectly for images, documents, and small videos (e.g., up to 300MB-500MB depending on the device).
        const fileBuffer = await file.arrayBuffer();

        // 3. Calculate the hash (returns an ArrayBuffer)
        const hashBuffer = await crypto.subtle.digest(algorithm, fileBuffer);

        // 4. Convert the ArrayBuffer to a Hexadecimal string
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");

        return hashHex;
    }

    /**
     * Encrypts a File object.
     * Returns a new File with a ".enc" extension and "application/octet-stream" type.
     */
    async encryptFile(file: File): Promise<File> {
        // 1. Read the file content into an ArrayBuffer
        const fileData = await file.arrayBuffer();
        const key = await this.getSKFromSession();
        const iv = this.getIVFromSession();

        // 2. Encrypt the data
        const encryptedBuffer = await window.crypto.subtle.encrypt(
            {
                name: this.symmetricAlgorithm.name,
                iv: iv as BufferSource
            },
            key,
            fileData
        );

        // 3. Create a new File object from the encrypted data
        // We append ".b3" to indicate it is encrypted
        const newFileName = file.name + ".b3";

        return new File([encryptedBuffer], newFileName, {
            type: "application/octet-stream",
            lastModified: Date.now()
        });
    }

    /**
     * Decrypts a File object.
     * Note: You should ideally know the original file name/type,
     * as encryption hides that metadata.
     */
    async decryptFile(
        encryptedFile: File,
        key: string,
        iv: string,
        originalName: string
    ): Promise<File> {
        try {
            // 1. Read the encrypted file content
            const encryptedData = await encryptedFile.arrayBuffer();

            // 2. Decrypt the data
            const decryptedBuffer = await window.crypto.subtle.decrypt(
                {
                    name: this.symmetricAlgorithm.name,
                    iv: this.getIVFromSession(iv) as BufferSource
                },
                await this.getSKFromSession(key),
                encryptedData
            );

            // 3. Reconstruct the original File
            // (In a real app, you might store the original MIME type alongside the file to restore it correctly here)
            return new File([decryptedBuffer], originalName, {
                type: "application/octet-stream", // Or the specific type if known (e.g. image/png)
                lastModified: Date.now()
            });
        } catch (error) {
            console.error(
                "Decryption failed. Check Key, IV, or file integrity."
            );
            throw error;
        }
    }

    /////

    private async encryptSymmetricKey(
        key: string,
        symmetricKey: ArrayBuffer
    ): Promise<ArrayBuffer> {
        const publicKey = await this.getPublicKey(key, "encrypt");

        // encryptedKeyBuffer
        return await window.crypto.subtle.encrypt(
            { name: this.encryptionAlgorithm.name },
            publicKey,
            symmetricKey
        );
    }

    /**
     * Encrypts a plaintext string using a hybrid encryption scheme.
     * 1. Generates a random AES-GCM symmetric key.
     * 2. Encrypts the plaintext with this AES key.
     * 3. Encrypts the AES key with the provided RSA public key.
     * 4. Packages the encrypted key, IV, and ciphertext into a single object.
     *
     * @param publicKey The recipient's RSA-OAEP public CryptoKey.
     * @param plainText The message to encrypt.
     * @returns A Promise that resolves to a HybridEncryptedData object.
     */
    async encrypt(
        key: string, // or by id ?
        plainText: string
    ): Promise<EncryptedData> {
        const symmetricKey = await window.crypto.subtle.generateKey(
            this.symmetricAlgorithm,
            true,
            this.symmetricAlgorithm.keyUsages
        );
        const iv = window.crypto.getRandomValues(new Uint8Array(12));

        const ciphertextBuffer = await window.crypto.subtle.encrypt(
            { name: this.symmetricAlgorithm.name, iv },
            symmetricKey,
            this.stringToArrayBuffer(plainText)
        );

        const rawSymmetricKey = await window.crypto.subtle.exportKey(
            "raw",
            symmetricKey
        );
        const encryptedSymmetricKey = await this.encryptSymmetricKey(
            key,
            rawSymmetricKey
        );
        // const encryptedSymmetricKey = await this.encryptSymmetricKey2(
        //     id,
        //     rawSymmetricKey
        // );

        return {
            encryptedSymmetricKey: this.arrayBufferToBase64Url(
                encryptedSymmetricKey
            ),
            encryptedData: this.arrayBufferToBase64Url(ciphertextBuffer),
            iv: Base64.fromUint8Array(iv, true) // iv is already a Uint8Array
        };
    }

    async encryptMessage(plainText: string): Promise<EncryptedMessage> {
        const symmetricKey = await window.crypto.subtle.generateKey(
            this.symmetricAlgorithm,
            true,
            this.symmetricAlgorithm.keyUsages
        );
        const iv = window.crypto.getRandomValues(new Uint8Array(12));

        const ciphertextBuffer = await window.crypto.subtle.encrypt(
            { name: this.symmetricAlgorithm.name, iv },
            symmetricKey,
            this.stringToArrayBuffer(plainText)
        );

        return {
            symmetricKey,
            encryptedData: this.arrayBufferToBase64Url(ciphertextBuffer),
            iv: Base64.fromUint8Array(iv, true) // iv is already a Uint8Array: true = base64url
        };
    }

    async encryptMessage2(
        symmetricKey: CryptoKey,
        iv: string,
        plainText: string
    ): Promise<string> {
        const ciphertextBuffer = await window.crypto.subtle.encrypt(
            {
                name: this.symmetricAlgorithm.name,
                iv: this.base64UrlToArrayBuffer(iv) as BufferSource // Base64.toUint8Array(iv) as BufferSource
            },
            symmetricKey,
            this.stringToArrayBuffer(plainText)
        );

        return this.arrayBufferToBase64Url(ciphertextBuffer); //encryptedData
    }

    async encryptKey(key: string, symmetricKey: CryptoKey): Promise<string> {
        const rawSymmetricKey = await window.crypto.subtle.exportKey(
            "raw",
            symmetricKey
        );

        const encryptedSymmetricKey = await this.encryptSymmetricKey(
            key,
            rawSymmetricKey
        );

        return this.arrayBufferToBase64Url(encryptedSymmetricKey);
    }

    ////

    private async decryptSymmetricKey(
        id: string,
        encryptedSymmetricKey: Uint8Array<ArrayBufferLike>
    ): Promise<ArrayBuffer> {
        const keyPair = await this.keys.get(id);
        if (!keyPair) throw new Error("Private key not found");

        const privateKey = await window.crypto.subtle.importKey(
            this.asymmetricAlgorithm.privateKeyEncoding.type,
            keyPair.privateKey,
            {
                name: this.encryptionAlgorithm.name,
                hash: this.asymmetricAlgorithm.hash
            },
            true,
            ["decrypt"]
        );

        return await window.crypto.subtle.decrypt(
            { name: this.encryptionAlgorithm.name },
            privateKey,
            encryptedSymmetricKey as BufferSource
        );
    }

    async decryptSymmetricKey2(
        id: string,
        encryptedSymmetricKey: string
    ): Promise<string> {
        try {
            const symmetricKeyRaw = await this.decryptSymmetricKey(
                id,
                this.base64UrlToArrayBuffer(encryptedSymmetricKey)
            );
            return this.arrayBufferToBase64Url(symmetricKeyRaw);
        } catch {
            return "";
        }
    }

    /**
     * Decrypts a HybridEncryptedData package.
     * 1. Decrypts the AES key using the RSA private key.
     * 2. Imports the decrypted AES key.
     * 3. Uses the AES key and IV to decrypt the ciphertext.
     *
     * @param privateKey The recipient's RSA-OAEP private CryptoKey.
     * @param encryptedPackage The encrypted data package.
     * @returns A Promise that resolves to the original plaintext string.
     */
    async decrypt(
        key: string,
        encryptedPackage: EncryptedData
    ): Promise<string | null> {
        const id = await this.getKeyId(key);
        if (id) {
            const symmetricKeyRaw = await this.decryptSymmetricKey(
                id,
                this.base64UrlToArrayBuffer(
                    encryptedPackage.encryptedSymmetricKey
                )
            );
            const symmetricKey = await window.crypto.subtle.importKey(
                "raw",
                symmetricKeyRaw,
                { name: this.symmetricAlgorithm.name },
                false,
                this.symmetricAlgorithm.keyUsages
            );
            const decryptedPlainTextBuffer = await window.crypto.subtle.decrypt(
                {
                    name: this.symmetricAlgorithm.name,
                    iv: Base64.toUint8Array(encryptedPackage.iv) as BufferSource // No .buffer needed here as AES-GCM takes a Uint8Array for the iv
                },
                symmetricKey,
                this.base64UrlToArrayBuffer(
                    encryptedPackage.encryptedData
                ) as BufferSource
            );

            return this.arrayBufferToString(decryptedPlainTextBuffer);
        }

        return null;
    }

    async decrypt2(
        key: string, // symmetric key
        iv: string,
        encryptedContent: string
    ): Promise<string> {
        const symmetricKey = await window.crypto.subtle.importKey(
            "raw",
            this.base64UrlToArrayBuffer(key) as BufferSource,
            { name: this.symmetricAlgorithm.name },
            false,
            this.symmetricAlgorithm.keyUsages
        );
        const decryptedPlainTextBuffer = await window.crypto.subtle.decrypt(
            {
                name: this.symmetricAlgorithm.name,
                iv: this.base64UrlToArrayBuffer(iv) as BufferSource
            },
            symmetricKey,
            this.base64UrlToArrayBuffer(encryptedContent) as BufferSource
        );

        return this.arrayBufferToString(decryptedPlainTextBuffer);
    }

    /// helpers

    private async singleHash(
        data: string | ArrayBuffer | Uint8Array
    ): Promise<Uint8Array> {
        let buffer: ArrayBuffer;

        if (typeof data === "string") {
            // used
            buffer = this.stringToArrayBuffer(data);
        } else if (data instanceof Uint8Array) {
            buffer = new Uint8Array(data).buffer;
        } else {
            buffer = new Uint8Array(data).buffer; // Handles ArrayBuffer or SharedArrayBuffer
        }

        const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
        return new Uint8Array(hashBuffer);
    }

    async hash(data: string) {
        const buffer = await this.singleHash(data);
        return Base64.fromUint8Array(buffer, true); // base 64 url safe
        //return this.arrayBufferToBase64Url(buffer);
    }

    // not used
    private async doubleHash(data: string) {
        const singleHash = await this.singleHash(data);
        return await this.singleHash(singleHash);
    }

    //Convert a string to an ArrayBuffer
    private stringToArrayBuffer(str: string): ArrayBuffer {
        const encoded = new TextEncoder().encode(str);
        return new Uint8Array(encoded).buffer; // This creates a real ArrayBuffer
    }

    // Convert an ArrayBuffer to a string
    private arrayBufferToString(buffer: ArrayBuffer): string {
        return new TextDecoder().decode(buffer);
    }

    // REFACTORED PART: Use js-base64 to convert ArrayBuffers to URL-safe Base64 strings.
    // The `true` argument enables URL-safe mode.
    private arrayBufferToBase64Url(buffer: ArrayBuffer): string {
        return Base64.fromUint8Array(new Uint8Array(buffer), true);
    }

    // REFACTORED PART: Use js-base64 to convert Base64 strings back to Uint8Arrays.
    // .buffer gets the underlying ArrayBuffer required by the crypto API.
    private base64UrlToArrayBuffer(
        base64url: string
    ): Uint8Array<ArrayBufferLike> {
        return new Uint8Array(Base64.toUint8Array(base64url).buffer);
    }

    private base64UrlToArrayBuffer2(base64url: string): ArrayBuffer {
        const uint8 = Base64.toUint8Array(base64url);
        return uint8.buffer.slice(
            uint8.byteOffset,
            uint8.byteOffset + uint8.byteLength
        ) as ArrayBuffer;
    }

    private binaryStringToArrayBuffer(binary: string): ArrayBuffer {
        const len = binary.length;
        const bytes = new Uint8Array(len); // Fixed typo: Uint8A -> Uint8Array
        for (let i = 0; i < len; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        return bytes.buffer;
    }

    private arrayBufferToBinaryString(buffer: ArrayBuffer): string {
        return String.fromCharCode.apply(
            null,
            Array.from(new Uint8Array(buffer))
        );
    }
}
